import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import {
  catchError,
  delay,
  map,
  Observable,
  of,
  retry,
  Subscriber,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { environment } from '@fb/environments';
import {
  ChatLanguage,
  ChatListItem,
  ChatMessageRole,
  ChatMessageStreamEvent,
  ChatMessage,
  Chat,
  CreateChatMessageReq,
  GetChatsQuery,
  GetChatsRes,
  GenerateChatTitleReq,
  PinChatReq,
  RenameChatReq,
  SubmitFeedbackReq,
  GetUserContextsRes,
} from '@fb/models';
import { AuthService } from '@fb/services/auth/auth.service';
import { AuthSessionError } from '@fb/services/auth/auth-session.error';
import { postAuthenticatedChatMessageStream } from '@fb/services/auth/authenticated-stream.fetch';

import {
  createChatMessageStreamMock,
  deleteChatMock,
  getChatsMock,
  getChatMock,
  generateChatTitleMock,
  pinChatMock,
  renameChatMock,
  getUserContextsMock,
} from './mocks';
import { AI_CHATS_PAGE_SIZE } from './ai-chat.constants';
import { ChatStreamFailure, toChatStreamFailure } from './helpers/chat-stream-failure';
import { SseParser } from './helpers/parse-sse';

/** HTTP statuses that mean the SSE endpoint is temporarily unavailable. */
const STREAM_UNAVAILABLE_STATUSES = new Set([404, 405, 501, 503]);

/**
 * A chat fetched right after it was created can 404 briefly — the backend's own write
 * (chat/message persistence) hasn't landed yet even though the stream already sent `final`.
 * Retries a few times with backoff before surfacing the error.
 */
const CHAT_NOT_FOUND_RETRY_COUNT = 3;
const CHAT_NOT_FOUND_RETRY_DELAY_MS = 500;

@Injectable()
export class AiChatFormService {
  #httpClient = inject(HttpClient);
  readonly #authService = inject(AuthService);

  #chatbotBaseUrl = `${environment.apiHost}ai-chatbot/`;
  /** Base URL for the chats API, served via the `ai-hr` gateway path. */
  #chatsBaseUrl = `${environment.apiHost}ai-hr/chats`;

  getUserContexts(): Observable<GetUserContextsRes> {
    if (environment.useMocks) {
      return of(getUserContextsMock()).pipe(delay(300));
    }

    return this.#httpClient.get<GetUserContextsRes>(
      `${this.#chatsBaseUrl}/contexts`,
    );
  }

  dryRequest(): Observable<void> {
    return this.#httpClient.get<void>(`${this.#chatbotBaseUrl}dry/`);
  }

  /**
   * Create message via SSE (`POST …/messages/stream`).
   * If streaming is unavailable before any answer content arrives, falls back to
   * `POST …/messages` (201 complete response) without issuing a second streamed reply.
   */
  createChatMessage(req: CreateChatMessageReq): Observable<ChatMessage> {
    if (environment.useMocks) {
      return createChatMessageStreamMock(req);
    }

    let hasAnswerContent = false;

    return this.#createChatMessageStream(req).pipe(
      tap((message) => {
        if (message.content) {
          hasAnswerContent = true;
        }
      }),
      catchError((error: unknown) => {
        if (hasAnswerContent) {
          if (error instanceof AuthSessionError) {
            return throwError(() => error);
          }

          return throwError(() => toChatStreamFailure(error, true));
        }

        if (!this.#isStreamUnavailable(error)) {
          return throwError(() => error);
        }

        return this.#createChatMessageComplete(req);
      }),
    );
  }

  /** Non-streaming fallback — POST /v1/chats/messages (201). */
  #createChatMessageComplete(
    req: CreateChatMessageReq,
  ): Observable<ChatMessage> {
    return this.#httpClient
      .post<ChatMessage>(`${this.#chatsBaseUrl}/messages`, req)
      .pipe(
        map((message) => ({
          ...message,
          isStreaming: false,
        })),
      );
  }

  #isStreamUnavailable(error: unknown): boolean {
    if (error instanceof ChatStreamFailure || error instanceof AuthSessionError) {
      return false;
    }

    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return (
      error.status === 0 || STREAM_UNAVAILABLE_STATUSES.has(error.status)
    );
  }

  #createChatMessageStream(
    req: CreateChatMessageReq,
  ): Observable<ChatMessage> {
    return new Observable<ChatMessage>((subscriber) => {
      const controller = new AbortController();

      void this.#runChatMessageStream(req, subscriber, controller.signal);

      return () => controller.abort();
    });
  }

  async #runChatMessageStream(
    req: CreateChatMessageReq,
    subscriber: Subscriber<ChatMessage>,
    signal: AbortSignal,
  ): Promise<void> {
    try {
      const body = await this.#fetchChatMessageStreamBody(req, signal);

      await this.#consumeChatMessageStream(req.chatId ?? '', body, subscriber);
      subscriber.complete();
    } catch (error) {
      if (signal.aborted) {
        subscriber.complete();

        return;
      }

      subscriber.error(error);
    }
  }

  async #fetchChatMessageStreamBody(
    req: CreateChatMessageReq,
    signal: AbortSignal,
  ): Promise<ReadableStream<Uint8Array>> {
    return postAuthenticatedChatMessageStream(
      `${this.#chatsBaseUrl}/messages/stream`,
      req,
      this.#authService,
      signal,
    );
  }

  async #consumeChatMessageStream(
    initialChatId: string,
    body: ReadableStream<Uint8Array>,
    subscriber: Subscriber<ChatMessage>,
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseParser();
    let chatId = initialChatId;
    let answer = '';
    let sawFinal = false;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      for (const raw of parser.push(decoder.decode(value, { stream: true }))) {
        const event = this.#parseStreamEvent(raw.event, raw.data);
        const result = this.#applyStreamEvent(event, chatId, answer, subscriber);

        chatId = result.chatId;
        answer = result.answer;
        sawFinal = sawFinal || result.sawFinal;
      }
    }

    if (!sawFinal) {
      throw new ChatStreamFailure(
        'Chat message stream ended without a final event',
        Boolean(answer),
      );
    }
  }

  #applyStreamEvent(
    event: ChatMessageStreamEvent,
    chatId: string,
    answer: string,
    subscriber: Subscriber<ChatMessage>,
  ): { chatId: string; answer: string; sawFinal: boolean } {
    switch (event.type) {
      case 'meta': {
        const nextChatId = event.chatId;

        subscriber.next(this.#streamingAgentMessage(nextChatId, answer));

        return { chatId: nextChatId, answer, sawFinal: false };
      }
      case 'answer_delta': {
        const nextAnswer = answer + event.text;

        subscriber.next(this.#streamingAgentMessage(chatId, nextAnswer));

        return { chatId, answer: nextAnswer, sawFinal: false };
      }
      case 'answer_replace':
        subscriber.next(this.#streamingAgentMessage(chatId, event.text));

        return { chatId, answer: event.text, sawFinal: false };
      case 'final':
        subscriber.next({
          ...event.message,
          internalStatus: event.message.internalStatus ?? undefined,
          isStreaming: false,
        });

        return { chatId, answer, sawFinal: true };
      case 'error':
        throw new ChatStreamFailure(event.message, event.hadContent ?? false);
    }
  }

  #streamingAgentMessage(chatId: string, content: string): ChatMessage {
    const now = new Date().toISOString();

    return {
      id: '',
      chatId,
      role: ChatMessageRole.Agent,
      content,
      language: ChatLanguage.En,
      createdAt: now,
      updatedAt: now,
      isStreaming: true,
      isFeedbackEligible: false,
    };
  }

  #parseStreamEvent(eventName: string, data: string): ChatMessageStreamEvent {
    const payload = JSON.parse(data) as Record<string, unknown>;
    const type = payload['type'] ?? eventName;

    switch (type) {
      case 'meta':
        return {
          type,
          chatId: String(payload['chatId'] ?? ''),
          phase: 'processing',
        };
      case 'answer_delta':
        return { type, text: String(payload['text'] ?? '') };
      case 'answer_replace':
        return { type, text: String(payload['text'] ?? '') };
      case 'final':
        return { type, message: payload['message'] as ChatMessage };
      case 'error':
        return {
          type,
          message: String(payload['message'] ?? ''),
          hadContent: Boolean(payload['hadContent']),
        };
      default:
        throw new Error(`Unknown chat stream event: ${String(type)}`);
    }
  }

  /** GET /v1/chats. Returns a paginated list of user's chats (ordered by most recent activity). */
  getChats(
    size = AI_CHATS_PAGE_SIZE,
    page = 1,
    query?: GetChatsQuery,
  ): Observable<GetChatsRes> {
    if (environment.useMocks) {
      return of(getChatsMock(query)).pipe(delay(500));
    }

    const params: Record<string, string | number> = {
      size,
      page,
      sort_by: 'updated_at',
      sort_order: 'desc',
    };
    if (query?.context) {
      params['context'] = query.context;
    }

    return this.#httpClient.get<GetChatsRes>(this.#chatsBaseUrl, {
      params,
    });
  }

  /** PATCH /v1/chats/{chatId}. Updates the chat title. */
  renameChat(chatId: string, title: string): Observable<ChatListItem> {
    if (environment.useMocks) {
      return of(renameChatMock(chatId, title)).pipe(delay(400));
    }

    return this.#httpClient.patch<ChatListItem>(
      `${this.#chatsBaseUrl}/${chatId}`,
      { title } satisfies RenameChatReq,
    );
  }

  /**
   * POST /v1/chats/{chatId}/title. Generates a title from the user message.
   *
   * Fired only after the stream's final event: the backend holds the new chat row in the
   * streaming request's transaction, which commits a moment after the last SSE byte, so the
   * first attempt can still 404 — retried with backoff. Best-effort beyond that: any other
   * failure resolves to `null` and the caller retries on the next successful send.
   */
  generateChatTitle(chatId: string, content: string): Observable<ChatListItem | null> {
    if (environment.useMocks) {
      return of(generateChatTitleMock(chatId, content)).pipe(delay(400));
    }

    return this.#httpClient
      .post<ChatListItem>(
        `${this.#chatsBaseUrl}/${chatId}/title`,
        { content } satisfies GenerateChatTitleReq,
      )
      .pipe(
        retry({
          count: CHAT_NOT_FOUND_RETRY_COUNT,
          delay: (error: unknown, retryCount) => {
            if (error instanceof HttpErrorResponse && error.status === 404) {
              return timer(CHAT_NOT_FOUND_RETRY_DELAY_MS * retryCount);
            }

            throw error;
          },
        }),
        catchError(() => of(null)),
      );
  }

  /** DELETE /v1/chats/{chatId}. Soft-deletes the chat. */
  deleteChat(chatId: string): Observable<void> {
    if (environment.useMocks) {
      deleteChatMock(chatId);

      return of(undefined).pipe(delay(300));
    }

    return this.#httpClient.delete<void>(`${this.#chatsBaseUrl}/${chatId}`);
  }

  /** PATCH /v1/chats/{chatId}. Updates the pin state of the chat. */
  pinChat(chatId: string, isPinned: boolean): Observable<ChatListItem> {
    if (environment.useMocks) {
      return of(pinChatMock(chatId, isPinned)).pipe(delay(300));
    }

    return this.#httpClient.patch<ChatListItem>(
      `${this.#chatsBaseUrl}/${chatId}`,
      { isPinned } satisfies PinChatReq,
    );
  }

  /**
   * PUT /v1/chats/messages/{messageId}/feedback.
   * Saves the answer rating and optional review. Passing `rating: null` clears the feedback.
   */
  submitFeedback(
    messageId: string,
    body: SubmitFeedbackReq,
  ): Observable<void> {
    if (environment.useMocks) {
      return of(undefined).pipe(delay(300));
    }

    return this.#httpClient.put<void>(
      `${this.#chatsBaseUrl}/messages/${messageId}/feedback`,
      body,
    );
  }

  /**
   * GET /v1/chats/{chatId}. Returns the full chat with all messages.
   * `retryOnNotFound` is opt-in for a chat the backend is still persisting right after creation.
   */
  getChat(
    chatId: string,
    { retryOnNotFound = false }: { retryOnNotFound?: boolean } = {},
  ): Observable<Chat> {
    if (environment.useMocks) {
      return of(getChatMock(chatId)).pipe(delay(600));
    }

    const chat$ = this.#httpClient.get<Chat>(`${this.#chatsBaseUrl}/${chatId}`);

    if (!retryOnNotFound) {
      return chat$;
    }

    return chat$.pipe(
      retry({
        count: CHAT_NOT_FOUND_RETRY_COUNT,
        delay: (error: unknown, retryCount) => {
          if (error instanceof HttpErrorResponse && error.status === 404) {
            return timer(CHAT_NOT_FOUND_RETRY_DELAY_MS * retryCount);
          }

          throw error;
        },
      }),
    );
  }
}
